import React from 'react';
import { View, Pressable } from 'react-native';

import Text from '~/client/components/Text';
import MusicProgressBar from '~/client/components/MusicProgressBar';
import { useMusicKit } from '~/client/utils/MusicKitContext';
import isWeb from '~/client/utils/isWeb';

function formatTime(totalSeconds: number) {
  if (!totalSeconds || Number.isNaN(totalSeconds)) {
    return '0:00';
  }

  const minutes = Math.floor(totalSeconds / 60);
  const seconds = Math.floor(totalSeconds % 60);

  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

export default function AppleMusicPlayerBar({
  catalogId,
  songName,
  artistName,
  textColor = '#FA4058',
  songId,
  isFavorite,
}: {
  catalogId?: string | null;
  songName: string;
  artistName: string;
  textColor?: string;
  songId?: string | number;
  isFavorite?: boolean;
}) {
  const musicKit = useMusicKit();

  // MusicKit JS is web-only, and there's nothing to play without a catalog id.
  if (!isWeb() || !catalogId) {
    return null;
  }

  const songMeta = songId
    ? { songId: String(songId), isFavorite: !!isFavorite }
    : undefined;

  const isCurrentSong = musicKit.nowPlayingCatalogId === catalogId;
  const isPlayingThisSong = isCurrentSong && musicKit.isPlaying;
  const isLoadingThisSong = musicKit.pendingCatalogId === catalogId;
  const progress =
    isCurrentSong && musicKit.currentPlaybackDuration > 0
      ? musicKit.currentPlaybackTime / musicKit.currentPlaybackDuration
      : 0;

  const handlePress = () => {
    if (isLoadingThisSong) {
      return;
    }
    if (isCurrentSong) {
      musicKit.togglePlayPause();
    } else {
      musicKit.playSong(catalogId, songMeta);
    }
  };

  return (
    <View
      className="flex flex-col rounded p-3 mb-4"
      style={{ backgroundColor: 'rgba(128,128,128,0.12)' }}
    >
      <View style={{ flexDirection: 'row', alignItems: 'center' }}>
        <Pressable
          disabled={!musicKit.isAvailable || isLoadingThisSong}
          onPress={handlePress}
          style={{
            marginRight: 12,
            opacity: musicKit.isAvailable ? 1 : 0.4,
            // @ts-expect-error `cursor` is a web-only style property
            cursor:
              musicKit.isAvailable && !isLoadingThisSong
                ? 'pointer'
                : 'default',
          }}
        >
          <Text style={{ color: textColor, fontSize: 24 }}>
            {isLoadingThisSong ? '⋯' : isPlayingThisSong ? '⏸' : '▶'}
          </Text>
        </Pressable>

        <View style={{ flex: 1 }}>
          <Text style={{ color: textColor, fontWeight: 'bold' }}>
            {songName}
          </Text>
          <Text style={{ color: textColor }}>{artistName}</Text>
        </View>

        {isCurrentSong ? (
          <Text style={{ color: textColor }}>
            {formatTime(musicKit.currentPlaybackTime)} /{' '}
            {formatTime(musicKit.currentPlaybackDuration)}
          </Text>
        ) : null}
      </View>

      {musicKit.isAvailable && !isCurrentSong ? (
        <View style={{ flexDirection: 'row', marginBottom: 4 }}>
          <Pressable
            onPress={() => musicKit.queueSong(catalogId, 'next', songMeta)}
            // @ts-expect-error `cursor` is a web-only style property
            style={{ marginRight: 16, cursor: 'pointer' }}
          >
            <Text style={{ color: textColor, fontSize: 12 }}>Play Next</Text>
          </Pressable>
          <Pressable
            onPress={() => musicKit.queueSong(catalogId, 'later', songMeta)}
            // @ts-expect-error `cursor` is a web-only style property
            style={{ cursor: 'pointer' }}
          >
            <Text style={{ color: textColor, fontSize: 12 }}>
              Add to Queue
            </Text>
          </Pressable>
        </View>
      ) : null}

      {isCurrentSong ? (
        <MusicProgressBar
          progress={progress}
          color={textColor}
          onSeek={(fraction) =>
            musicKit.seekToTime(fraction * musicKit.currentPlaybackDuration)
          }
        />
      ) : null}

      {musicKit.error ? (
        <Text style={{ color: textColor, fontSize: 12, marginTop: 6 }}>
          {musicKit.error}
        </Text>
      ) : null}
    </View>
  );
}
